/*
  Что должник может сделать: собрать рассрочку и заявить о своём.

  Обе части только передают выбор на сервер и показывают его ответ. График
  пересчитывается сервером при каждом изменении — не потому, что это дёшево, а
  потому что границы рассрочки устанавливает организация, и клиент не вправе
  ни знать их наизусть, ни толковать.
*/

import { useEffect, useState } from 'react'

import { Notice } from './Common.jsx'
import { date, money } from '../api/format.js'
import { parseCount } from '../api/parseCount.js'

const EXIT_TITLES = {
  hardship: 'У меня трудная жизненная ситуация',
  dispute: 'Я не согласен с суммой',
}

export function Instalment({ offer, api, onDone }) {
  // Поля хранят набранный текст, а не число: «6х» или пустое поле раньше становились
  // нулём, и график считался для срока, которого человек не вводил (строка 299 реестра).
  const [monthsText, setMonthsText] = useState(String(Math.min(6, offer.max_months ?? 6)))
  const [dayText, setDayText] = useState('10')
  // График хранится вместе с условиями, для которых он посчитан (аудит 26.10.2026,
  // причина 5): запоздавший ответ на прежние значения не подменяет свежий, а кнопка
  // «Оформить» не уходит с графиком, которого человек не видел.
  const [shown, setShown] = useState({ key: null, plan: null })
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const available = offer.available
  const parsedMonths = parseCount(monthsText, { min: 1, max: offer.max_months })
  const parsedDay = parseCount(dayText, { min: 1, max: 28 })
  const months = parsedMonths.value
  const day = parsedDay.value
  // Пустое поле - не число: срок и день обязательны (parseCount пустое пропускает).
  const typed = months !== null && day !== null
  const key = typed ? `${months}:${day}` : null
  const plan = typed ? shown.plan : null
  const fresh = key !== null && shown.key === key

  useEffect(() => {
    if (!available || !typed) return undefined
    let current = true
    setError(null)
    api
      .previewInstalment(months, day)
      .then((answer) => {
        if (current) setShown({ key: `${months}:${day}`, plan: answer })
      })
      .catch((failure) => {
        if (!current) return
        setShown({ key: null, plan: null })
        setError(failure)
      })
    return () => {
      current = false
    }
  }, [available, api, typed, months, day])

  if (!available) {
    // Молча спрятанная кнопка читается как поломка. Причина называется.
    return (
      <div className="card">
        <div className="section__title">Не могу заплатить сразу</div>
        <div className="muted">{offer.unavailable_reason}</div>
      </div>
    )
  }

  const accept = async () => {
    if (!fresh) return
    setBusy(true)
    setError(null)
    try {
      await api.acceptInstalment(months, day)
      onDone()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="section__title">Не могу заплатить сразу - оформить рассрочку</div>

      <div className="filters">
        <label className="field">
          <span className="field__label">На сколько месяцев</span>
          <input
            className="field__input"
            inputMode="numeric"
            value={monthsText}
            aria-invalid={parsedMonths.error || !monthsText.trim() ? 'true' : undefined}
            onChange={(event) => setMonthsText(event.target.value)}
          />
          <span className="muted">не больше {offer.max_months}</span>
          {parsedMonths.error ? <span className="field__error">{parsedMonths.error}</span> : null}
        </label>
        <label className="field">
          <span className="field__label">Удобный день платежа</span>
          <input
            className="field__input"
            inputMode="numeric"
            value={dayText}
            aria-invalid={parsedDay.error || !dayText.trim() ? 'true' : undefined}
            onChange={(event) => setDayText(event.target.value)}
          />
          <span className="muted">с 1-го по 28-е число</span>
          {parsedDay.error ? <span className="field__error">{parsedDay.error}</span> : null}
        </label>
      </div>

      <Notice error={error} />

      {plan ? (
        <>
          <table className="table">
            <tbody>
              {plan.payments.map((item, index) => (
                <tr key={item.due_on}>
                  <td>Платёж {index + 1}</td>
                  <td>{date(item.due_on)}</td>
                  <td className="table__num">{money(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="muted">{plan.explanation}</div>
          {plan.warnings.map((warning) => (
            <Notice key={warning} kind="warn">
              <div className="notice__what">{warning}</div>
            </Notice>
          ))}
          <button type="button" className="button" disabled={busy || !fresh} onClick={accept}>
            {busy ? 'Оформляем…' : fresh ? 'Оформить рассрочку' : 'Пересчитываем график…'}
          </button>
          <div className="muted">
            Оформление подтверждает, что вы знаете о долге. Первый платёж закрепляет
            договорённость: после него рассрочка действует.
          </div>
        </>
      ) : null}
    </div>
  )
}

export function Exits({ api, onDone }) {
  const [open, setOpen] = useState(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const send = async () => {
    setBusy(true)
    setError(null)
    try {
      await (open === 'hardship' ? api.hardship(note) : api.dispute(note))
      setOpen(null)
      setNote('')
      onDone()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="section__title">Другое</div>
      {open === null ? (
        <div className="row">
          {Object.entries(EXIT_TITLES).map(([key, title]) => (
            <button
              key={key}
              type="button"
              className="button button--quiet"
              onClick={() => setOpen(key)}
            >
              {title}
            </button>
          ))}
        </div>
      ) : (
        <>
          <label className="field">
            <span className="field__label">{EXIT_TITLES[open]}</span>
            <textarea
              className="field__input"
              rows="3"
              maxLength="1000"
              value={note}
              placeholder="Коротко опишите ситуацию - это увидит сотрудник организации"
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <Notice error={error} />
          <div className="row">
            <button type="button" className="button" disabled={busy} onClick={send}>
              {busy ? 'Отправляем…' : 'Отправить'}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setOpen(null)}>
              Отмена
            </button>
          </div>
        </>
      )}
    </div>
  )
}
